'use client';

// ИСПОЛНИТЕЛЬ ПЛАНА ПРОГРЕВА САЙТА — ОДНА ОЧЕРЕДЬ (ТЗ ~/projects/HELP-WARM-FIX-TZ.md §2, владелец 26.10.2026: «нигде не
// перечислена вся логика, в каком порядке прогрев»). ЧТО и НА КАКОМ УРОВНЕ греть — только файл-план
// `lib/app-pages/warm-plan.ts` (`SITE_WARM_TARGETS`, `SITE_WARM_PAGES`, `SITE_WARM_CABINET`); здесь — только КАК: очередь,
// числа, ворота. Страница называет цель плана (`warmTarget('<id>', ключ, адрес)`), уровень и порядок берутся из плана.
//
// ЧИСЛА — `WARM_QUEUE` плана (PREFETCH-CAREFUL-TZ §2, владелец 11.10.2026: «аккуратный прогрев, чатов миллионы»):
//   • ОДИН запрос прогрева за раз, шаг 300 мс между запусками (§2 п.3);
//   • старт — после первого экрана: страница загрузилась (`load`) и браузер в простое (`requestIdleCallback`);
//   • полосы: 0 — касание (сразу, мимо всех ворот), 1 — У1 (вход: справка, ниша, сцена, пасхалка), 2 — У2 (видимое
//     на открытом экране), 3 — У3 (в простой: каждый запуск ждёт простоя браузера);
//   • КАСАНИЕ ГЛАВНЕЕ ПРОГРЕВА (§2 п.2): пока в полёте хоть один запрос ОТКРЫТОГО экрана (`beginForeground` — первая
//     загрузка `usePersistentData`, `awaitPersistent`, срочный прогрев), фон У2/У3 новых запросов не запускает — пауза,
//     не сброс; предохранитель — `foregroundMaxMs`. У1 («на входе, сразу») ждёт только касание;
//   • медленная сеть (`saveData` или `slow-2g`/`2g`) — фон стоит (§2 п.4), касания идут;
//   • вкладка скрыта (`visibilitychange`) — очередь стоит, вернулись — продолжает;
//   • ключ, прогретый меньше `recheckMs` назад или ещё верный (данные свежие / той же версии сайта), не греется;
//   • ФОН (полосы 1–3) — ещё и по ВОЗРАСТУ КОПИИ (ТЗ ~/projects/SPEED-TZ.md §5.4): копия моложе `warmFreshMs` плана
//     (списки 15 мин, прочее 60 мин) в очередь не ставится, а встав — снимается перед запуском, если экран уже взял ключ
//     сам; перезагрузка страницы поэтому годные копии не перегревает;
//   • БЮДЖЕТ полос 2–3 на загрузку страницы (`WARM_QUEUE.budget`, §5.4): кончился — строка журнала `warm-budget`,
//     полосы 2–3 стоят до перезагрузки; полоса 1 и касание — без бюджета;
//   • ключ, который экран сам держит в полёте (`beginForeground(key)`), фоном не ставится; адрес с `fast=1` не греется
//     никогда (§5.5); запись страницы `when: 'bot-window'` — только при открытом окне бота (§5.3);
//   • цель `afterScreen` (первый ролик, §6.4) — полоса 1, но после запросов открытого экрана;
//   • данные кладутся в ТОТ ЖЕ кэш (`prefetchPersistent`) — экран, открытый позже, берёт их тем же кадром.
// Задания без ключа (картинки статей, фото сцены, ролик пасхалки) — `warmTargetRun`: та же очередь, тот же шаг.
import { log as logJournal } from './client-journal';
import {
  BOT_WINDOW_PATH,
  SITE_BOTS_PAGE,
  SITE_WARM_PAGES,
  SITE_WARM_TARGETS,
  WARM_QUEUE,
  siteLevel,
  warmFreshMs,
  type SiteWarmPage,
  type SiteWarmTargetId,
  type WarmLevel,
} from './app-pages/warm-plan';
import { persistentCurrent, persistentFailed, prefetchPersistent } from './use-persistent-data';

/** Шаг между запусками прогрева (PREFETCH-CAREFUL §2 п.3, владелец 11.10) — из плана. */
export const WARM_STEP_MS = WARM_QUEUE.stepMs;
/** Не больше стольких запросов фонового прогрева одновременно (§2 п.3: один) — из плана. */
export const WARM_PARALLEL = WARM_QUEUE.parallel;
/** Предохранитель паузы: один запрос открытого экрана держит фон не дольше стольких мс (§2 п.2) — из плана. */
export const FOREGROUND_MAX_MS = WARM_QUEUE.foregroundMaxMs;

type WarmOpts = { accept?: (raw: unknown) => boolean; version?: string };
/** Полоса очереди: 0 — касание, 1–3 — уровни плана. Внутри полосы — по порядку постановки. */
type Lane = 0 | WarmLevel;
type Job = {
  key: string;
  url: string;
  opts: WarmOpts;
  lane: Lane;
  target: SiteWarmTargetId;
  run?: () => Promise<unknown>;
  /** Полоса 1 после запросов открытого экрана (`afterScreen` цели плана). */
  afterScreen?: boolean;
};

const queue: Job[] = [];
/** Ключ → его задание в очереди (дубль не ставится; касание подменяет фоновое задание срочным). */
const queued = new Map<string, Job>();
/** Когда ключ последний раз ушёл в прогрев — повтор не чаще `warmedMs`. */
const warmedAt = new Map<string, number>();
/** Фоновые задания прогрева в полёте (срочные сюда не входят — они передний план). */
let running = 0;
/** Из них — уровня 1 (У1). */
let runningL1 = 0;
/** Ждут конца У1 (тяжёлое со своим исполнителем — скрытые кадры сцен, `afterLevel1`). */
const afterL1: Array<() => void> = [];
let timer: number | null = null;
let lastStart = 0;
let gateOpen = false;
let gateArmed = false;
let hooked = false;
/** У3 — «в простой»: следующий запуск полосы 3 ждёт простоя браузера (requestIdleCallback). */
let idleGranted = false;
let idleAsked = false;
/** Запросы ОТКРЫТОГО экрана в полёте: токен → когда начался и чей ключ (§2 п.2; ключ — SPEED §5.5). */
const foreground = new Map<number, { at: number; key?: string }>();
let fgSeq = 0;

// ── БЮДЖЕТ ФОНА полос 2–3 на загрузку страницы (SPEED §5.4) ──
let budgetRequests = 0;
let budgetBytes = 0;
let budgetOut = false;
/** Адреса прогрева в полёте → их байты по проводу приносит наблюдатель ресурсов (буфер `performance` может быть полон). */
const budgetUrls = new Set<string>();
let budgetObserver: PerformanceObserver | null = null;

function absUrl(url: string): string {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return url;
  }
}

function watchBudgetBytes(url: string): void {
  if (typeof PerformanceObserver === 'undefined') return;
  budgetUrls.add(absUrl(url));
  if (budgetObserver) return;
  try {
    budgetObserver = new PerformanceObserver((list) => {
      for (const e of list.getEntries() as PerformanceResourceTiming[]) {
        if (!budgetUrls.delete(e.name)) continue;
        budgetBytes += e.transferSize || e.encodedBodySize || 0;
      }
    });
    budgetObserver.observe({ type: 'resource', buffered: false });
  } catch {
    budgetObserver = null;
  }
}

/** Бюджет кончился — строка журнала один раз, задания полос 2–3 снимаются (до перезагрузки страницы). */
function budgetSpent(): boolean {
  if (budgetOut) return true;
  const b = WARM_QUEUE.budget;
  if (budgetRequests < b.requests && budgetBytes < b.bytes) return false;
  budgetOut = true;
  let dropped = 0;
  for (let i = queue.length - 1; i >= 0; i -= 1) {
    const j = queue[i]!;
    if (j.lane < 2) continue;
    queue.splice(i, 1);
    queued.delete(j.key);
    dropped += 1;
  }
  logJournal('warm-budget', { requests: budgetRequests, bytes: budgetBytes, dropped });
  return true;
}

// ── ВОЗРАСТ КОПИИ (SPEED §5.4): время записи копий на устройстве — `scriptera:cache-at` хука данных ──
const CACHE_AT_KEY = 'scriptera:cache-at';
let atMap: Record<string, number> = {};
let atReadAt = -Infinity;
function copyAt(key: string): number {
  const now = Date.now();
  // Карта читается не чаще раза в 500 мс: очередь спрашивает её на каждую постановку.
  if (now - atReadAt > 500) {
    atReadAt = now;
    try {
      const raw = window.localStorage.getItem(CACHE_AT_KEY);
      const got = raw ? (JSON.parse(raw) as unknown) : null;
      atMap = got && typeof got === 'object' ? (got as Record<string, number>) : {};
    } catch {
      atMap = {};
    }
  }
  const at = atMap[key];
  return typeof at === 'number' && Number.isFinite(at) ? at : 0;
}

/** Фону ключ не нужен: копия моложе срока плана или данные ещё верные (свежие / той же версии сайта). */
function freshForBackground(job: Pick<Job, 'key' | 'url' | 'opts' | 'run'>): boolean {
  if (job.run) return false;
  const at = copyAt(job.key);
  if (at > 0 && Date.now() - at < warmFreshMs(job.key, job.url)) return true;
  return persistentCurrent(job.key, job.url, job.opts.version);
}

/** Экран сам держит этот ключ в полёте (SPEED §5.5). */
function foregroundHas(key: string): boolean {
  for (const f of foreground.values()) if (f.key === key) return true;
  return false;
}

/**
 * УСЛОВИЕ ЗАПИСИ СТРАНИЦЫ (SPEED §5.3): цель с `when: 'bot-window'` на «Ботах» фоном греется только при открытом окне
 * бота (`/app/bots/<id>`); на других страницах запись «Ботов» не действует. Касание — мимо этой проверки.
 */
export function warmWhenOk(target: SiteWarmTargetId, pathname?: string): boolean {
  const entry = SITE_WARM_PAGES[SITE_BOTS_PAGE]?.targets.find((e) => e.id === target);
  if (entry?.when !== 'bot-window') return true;
  const p = pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  if (!/\/app\/bots(?:[/?#]|$)/.test(p)) return true;
  return BOT_WINDOW_PATH.test(p);
}

/** Поставить задание в его полосу: после всех заданий той же и более срочных полос. */
function enqueue(job: Job): void {
  const at = queue.findIndex((j) => j.lane > job.lane);
  if (at < 0) queue.push(job);
  else queue.splice(at, 0, job);
}

function hidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

/**
 * МЕДЛЕННАЯ СЕТЬ — ФОНА НЕТ (PREFETCH-CAREFUL §2 п.4, `WARM_QUEUE.slowNetwork`): человек просил экономить трафик
 * (`saveData`) или сеть `slow-2g`/`2g`. Касания идут всё равно. Нет `navigator.connection` (Safari, Firefox) — греем.
 */
export function warmAllowed(): boolean {
  if (typeof navigator === 'undefined') return true;
  const c = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!c) return true;
  if (c.saveData === true) return WARM_QUEUE.slowNetwork;
  return WARM_QUEUE.slowNetwork || (c.effectiveType !== 'slow-2g' && c.effectiveType !== '2g');
}

/**
 * ЗАПРОС ОТКРЫТОГО ЭКРАНА НАЧАЛСЯ (§2 п.2): пока такие в полёте, фон новых запросов не запускает. Вернуть токен
 * в `endForeground`, когда ответ пришёл (или упал). Предохранитель: через FOREGROUND_MAX_MS токен паузу больше не держит.
 */
export function beginForeground(key?: string): number {
  holdUntil = 0; // запрос экрана начался — дальше паузу держит он сам
  fgSeq += 1;
  foreground.set(fgSeq, key ? { at: Date.now(), key } : { at: Date.now() });
  return fgSeq;
}

/** Запрос открытого экрана закончился — фон продолжает (если других таких нет). */
export function endForeground(token: number): void {
  if (!foreground.delete(token)) return;
  pump();
}

/** Сколько ещё мс фон ждёт запросы открытого экрана (0 — не ждёт). Протухшие по предохранителю — снимаются. */
function foregroundWait(now: number): number {
  let wait = 0;
  for (const [tok, { at }] of foreground) {
    const left = at + FOREGROUND_MAX_MS - now;
    if (left <= 0) foreground.delete(tok);
    else wait = Math.max(wait, left);
  }
  return wait;
}

type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };

/** Ворота «после первого экрана»: загрузка страницы закончилась и браузер в простое. */
function armGate(): void {
  if (gateArmed || typeof window === 'undefined') return;
  gateArmed = true;
  const open = () => {
    gateOpen = true;
    pump();
  };
  const idle = () => {
    const w = window as IdleWindow;
    if (w.requestIdleCallback) w.requestIdleCallback(open, { timeout: WARM_QUEUE.idleTimeoutMs });
    else window.setTimeout(open, WARM_STEP_MS);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}

/** У3: попросить у браузера простой для следующего запуска полосы 3. */
function askIdle(): void {
  if (idleAsked || typeof window === 'undefined') return;
  idleAsked = true;
  const grant = () => {
    idleAsked = false;
    idleGranted = true;
    pump();
  };
  const w = window as IdleWindow;
  if (w.requestIdleCallback) w.requestIdleCallback(grant, { timeout: WARM_QUEUE.idleTimeoutMs });
  else window.setTimeout(grant, WARM_STEP_MS);
}

/** Вернулись на вкладку или сеть стала быстрее — очередь продолжает. */
function hookEvents(): void {
  if (hooked || typeof document === 'undefined') return;
  hooked = true;
  document.addEventListener('visibilitychange', () => {
    if (!hidden()) pump();
  });
  const c = (navigator as unknown as { connection?: EventTarget }).connection;
  c?.addEventListener?.('change', () => pump());
}

function schedule(delay: number): void {
  if (timer !== null || typeof window === 'undefined') return;
  timer = window.setTimeout(() => {
    timer = null;
    pump();
  }, delay);
}

function start(job: Job): void {
  queue.splice(queue.indexOf(job), 1);
  queued.delete(job.key);
  const urgent = job.lane === 0;
  // Пока задание ждало, экран мог взять ключ сам — фону он больше не нужен (SPEED §5.5: один ключ — один запрос).
  if (!urgent && (foregroundHas(job.key) || freshForBackground(job))) return;
  const now = Date.now();
  warmedAt.set(job.key, now);
  // Срочный прогрев (касание) — сам передний план: фон ждёт его, как ждёт первую загрузку экрана.
  const tok = urgent ? beginForeground(job.key) : 0;
  if (!urgent) {
    running += 1;
    if (job.lane === 1) runningL1 += 1;
    lastStart = now;
    if (job.lane === 3) idleGranted = false;
    if (job.lane >= 2) {
      budgetRequests += 1;
      if (!job.run) watchBudgetBytes(job.url);
    }
  }
  const work: Promise<unknown> = job.run ? job.run() : prefetchPersistent(job.key, job.url, job.opts);
  void work
    .catch(() => undefined)
    .finally(() => {
      noteAdmWarm(job.key, now);
      if (urgent) endForeground(tok);
      else {
        running -= 1;
        if (job.lane === 1) runningL1 -= 1;
        pump();
      }
    });
}

/** ЖУРНАЛ ПРОГРЕВА Scriptera A (ТЗ ~/projects/ADM-APP-FIX-4-TZ.md §2.8 шаг 9): каждый завершённый прогрев ключа
 *  `adm:*` — строка `adm:warm {key, ms, ok}`. Только префикс `adm:` — прогревы клиентского кабинета журнал не
 *  засоряют. Ключ режется до 60 знаков (как `data.fast`). */
export const ADM_WARM_PREFIX = 'adm:';
function noteAdmWarm(key: string, startedAt: number): void {
  if (!key.startsWith(ADM_WARM_PREFIX)) return;
  logJournal('adm:warm', { key: key.slice(0, 60), ms: Date.now() - startedAt, ok: !persistentFailed(key) });
}

/** ЩЕЛЬ МЕЖДУ КАСАНИЕМ И ЗАПРОСОМ ЭКРАНА (приёмка 11.10): после касания ряда экран монтируется и шлёт свой запрос
 *  через ~0,1–0,3 с; если срочный прогрев по касанию не ушёл (ключ прогрет только что), за это время шаг очереди
 *  выпускал фоновый прогрев. Поэтому касание держит фон до TOUCH_HOLD_MS или до первого запроса переднего плана. */
export const TOUCH_HOLD_MS = WARM_QUEUE.touchHoldMs;
let holdUntil = 0;

/** У1 позади: ворота открыты, заданий У1 (и касаний) нет ни в очереди, ни в полёте — отпустить ждущих. */
function releaseL1(): void {
  if (!gateOpen || runningL1 > 0 || afterL1.length === 0) return;
  if (queue.some((j) => j.lane <= 1)) return;
  for (const fn of afterL1.splice(0)) fn();
}

function pump(): void {
  releaseL1();
  if (queue.length === 0 || hidden()) return;
  // Срочные (касание) стоят в начале очереди и идут сразу, мимо всех ворот.
  while (queue[0]?.lane === 0) start(queue[0]!);
  let head = queue[0];
  if (!head || !gateOpen || running >= WARM_PARALLEL || !warmAllowed()) return;
  // Бюджет полос 2–3 кончился — их задания сняты (SPEED §5.4).
  if (head.lane >= 2 && budgetSpent()) return pump();
  const now = Date.now();
  const fgBusy = foregroundWait(now);
  // Первый ролик (`afterScreen`) ждёт запросы открытого экрана; прочие задания У1 тем временем идут (SPEED §6.4).
  if (head.lane === 1 && head.afterScreen && fgBusy > 0) head = queue.find((j) => j.lane === 1 && !j.afterScreen) ?? head;
  // У1 («на входе, сразу») запросы открытого экрана не ждёт — только касание (удержание после тапа): на первом входе
  // с устройства каждый экран грузится «с нуля», и пауза держала справку до 10–20 с (замер 26.10.2026: окно статьи
  // из «Создать бота» шло в сеть за оглавлением). Шаг и «один за раз» — как у всех.
  const fgWait = Math.max(head.lane === 1 && !head.afterScreen ? 0 : fgBusy, holdUntil - now);
  if (fgWait > 0) return schedule(fgWait);
  const wait = lastStart + WARM_STEP_MS - now;
  if (wait > 0) return schedule(wait);
  // У3 — только в простой браузера.
  if (head.lane === 3 && !idleGranted) return askIdle();
  start(head);
  if (queue.length > 0) schedule(WARM_STEP_MS);
}

/** Цель плана идёт после запросов открытого экрана (`afterScreen`, SPEED §6.4). */
function afterScreenOf(target: SiteWarmTargetId): boolean {
  return (SITE_WARM_TARGETS[target] as { afterScreen?: true }).afterScreen === true;
}

/** Поставить задание (общая часть `warmTarget`/`warmTargetRun`). */
function put(job: Omit<Job, 'lane'>, lane: Lane): void {
  if (typeof window === 'undefined' || !job.key) return;
  armGate();
  hookEvents();
  // Удержание фона ставится ДО проверки свежести: касание по только что прогретому ключу запроса не создаёт,
  // а щель до запроса экрана закрыть всё равно надо.
  if (lane === 0) holdUntil = Date.now() + TOUCH_HOLD_MS;
  // Быстрый ответ (`fast=1`) — ускорение первого показа экрана, не прогрев (SPEED §5.5, замок warm-plan.site.test.ts).
  if (/[?&]fast=1(?:&|$)/.test(job.url)) return;
  const at = warmedAt.get(job.key);
  if (at !== undefined && Date.now() - at < WARM_QUEUE.warmedMs) return;
  if (lane > 0) {
    // Фон: условие записи страницы, бюджет, ключ в полёте у экрана, годная копия (SPEED §5.3–5.5).
    if (!warmWhenOk(job.target)) return;
    if (lane >= 2 && budgetOut) return;
    if (foregroundHas(job.key)) return;
    if (freshForBackground(job)) return;
  } else if (!job.run && persistentCurrent(job.key, job.url, job.opts.version)) {
    // Касание: данные ключа ещё верные (свежие или той же версии сайта) — запроса не нужно.
    return;
  }
  const prev = queued.get(job.key);
  if (prev) {
    // Ключ уже ждёт в менее срочной полосе — переезжает (касание — первым в очереди, со своим адресом).
    if (lane < prev.lane) {
      queue.splice(queue.indexOf(prev), 1);
      const next: Job = { ...job, lane };
      queued.set(job.key, next);
      if (lane === 0) queue.unshift(next);
      else enqueue(next);
    }
    pump();
    return;
  }
  const next: Job = { ...job, lane, ...(lane === 1 && afterScreenOf(job.target) ? { afterScreen: true } : {}) };
  queued.set(job.key, next);
  // Касание — в самое начало (последнее касание — первым); прочее — в конец своей полосы.
  if (lane === 0) queue.unshift(next);
  else enqueue(next);
  pump();
}

/**
 * ПРОГРЕТЬ ЦЕЛЬ ПЛАНА: положить ответ `url` в кэш ключа `key`, пока экран ещё не открыт. Уровень — из плана
 * (`SITE_WARM_TARGETS[target].level`); `urgent` — касание/наведение: в начало очереди и мимо всех ворот. Ключ уже в
 * очереди фоном — касание подменяет задание своим адресом (у ленты переписки фон лёгкий `?limit=20`, касание — полная).
 */
export function warmTarget(target: SiteWarmTargetId, key: string, url: string, opts: WarmOpts = {}, urgent = false): void {
  if (!url) return;
  put({ key, url, opts, target }, urgent ? 0 : SITE_WARM_TARGETS[target].level);
}

/**
 * ЗАДАНИЕ ЦЕЛИ БЕЗ КЛЮЧА КЭША (картинки статей, фото сцены, ролик пасхалки): `name` — память повторов (как ключ),
 * `run` — работа, промис — её конец (очередь ждёт его, как ждёт ответ ручки). Уровень — из плана.
 */
export function warmTargetRun(target: SiteWarmTargetId, name: string, run: () => Promise<unknown>, urgent = false): void {
  put({ key: `run:${name}`, url: '', opts: {}, target, run }, urgent ? 0 : SITE_WARM_TARGETS[target].level);
}

/** ИСПОЛНИТЕЛЬ СТРАНИЦЫ (только `components/cabinet/warm-cache.tsx`): цель на уровне, который ей дала запись страницы
 *  плана (`SiteWarmPage`), — напр. «Подписка» на «Аккаунте» У2, а на входе в кабинет У3. */
export function warmPageTarget(
  page: SiteWarmPage,
  target: SiteWarmTargetId,
  job: { key: string; url: string; opts?: WarmOpts } | { name: string; run: () => Promise<unknown> },
): void {
  const lane = siteLevel(target, page);
  if ('run' in job) put({ key: `run:${job.name}`, url: '', opts: {}, target, run: job.run }, lane);
  else if (job.url) put({ key: job.key, url: job.url, opts: job.opts ?? {}, target }, lane);
}

/**
 * ПОСЛЕ У1 (ТЗ HELP-WARM-FIX §2: «важное и маленькое — сразу, остальное потом»): тяжёлое со своим исполнителем
 * (скрытые кадры сцен мастера — по странице приложения на кадр) стартует, когда У1 плана (справка, ниша, фото сцены,
 * пасхалка) уже в кэше. Замер 26.10.2026: восемь кадров разом занимали все соединения браузера, и оглавление справки
 * ждало отправки 18 с.
 */
export function afterLevel1(fn: () => void): void {
  if (typeof window === 'undefined') return;
  afterL1.push(fn);
  armGate();
  hookEvents();
  pump();
}

/**
 * ОЧЕРЕДЬ ДАННЫХ ПУСТА И ЭКРАН НЕ ГРУЗИТСЯ — можно тяжёлое фоновое (остальные ролики пасхалки, CLAIMS-1020 §8.3,
 * help-video-cache.ts): нет запросов открытого экрана (§2 п.2), нет удержания после касания, прогрев не идёт и не ждёт.
 */
export function warmQuiet(): boolean {
  const now = Date.now();
  return foregroundWait(now) === 0 && holdUntil <= now && running === 0 && queue.length === 0;
}

/** Ответ ручки принят, если `ok: true` — так у всех ручек кабинета. */
export const okAccept = (j: unknown): boolean => !!(j as { ok?: boolean } | null)?.ok;

/** Только для тестов: что сейчас в очереди (ключ и полоса), по порядку. */
export function warmQueueForTests(): Array<{ key: string; lane: number }> {
  return queue.map((j) => ({ key: j.key, lane: j.lane }));
}

/** Только для тестов: очистить очередь и память прогрева. */
export function resetWarmForTests(): void {
  queue.length = 0;
  queued.clear();
  warmedAt.clear();
  foreground.clear();
  running = 0;
  runningL1 = 0;
  afterL1.length = 0;
  if (timer !== null) window.clearTimeout(timer);
  timer = null;
  lastStart = 0;
  gateOpen = false;
  gateArmed = false;
  idleGranted = false;
  idleAsked = false;
  holdUntil = 0;
  budgetRequests = 0;
  budgetBytes = 0;
  budgetOut = false;
  budgetUrls.clear();
  atMap = {};
  atReadAt = -Infinity;
}
